/**
 * Compliance Momentum card: shows whether compliance score improved, declined, or
 * stayed flat over the last 30 days, with delta and plain-English summary.
 * Operator-friendly, non-punitive language.
 */
import React from 'react';
import { Card, CardHeader, CardBody } from '../ui';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

function getMomentum(latest, previous) {
  const curr = latest?.complianceScore != null ? Number(latest.complianceScore) : null;
  const prev = previous?.complianceScore != null ? Number(previous.complianceScore) : null;
  if (curr == null || prev == null) return { direction: 'flat', delta: 0, summary: 'Not enough history yet. Keep logging data to see your momentum.' };
  const delta = Math.round(curr - prev);
  if (delta > 0) return { direction: 'up', delta, summary: `You improved ${delta} point${delta === 1 ? '' : 's'} this month.` };
  if (delta < 0) return { direction: 'down', delta, summary: `You're ${Math.abs(delta)} point${delta === -1 ? '' : 's'} below last month. Small steps count—renew one doc or update one record to start moving back up.` };
  return { direction: 'flat', delta: 0, summary: 'You held steady this month. No change is still a win when things stay in order.' };
}

export default function ComplianceMomentumCard({ snapshots, loading }) {
  const latest = snapshots?.[0];
  const previous = snapshots?.length >= 2 ? snapshots[snapshots.length - 1] : null; // oldest in window (e.g. ~30 days ago)
  const momentum = getMomentum(latest, previous);

  if (loading) {
    return (
      <Card className="analytics-card compliance-momentum-card">
        <CardBody>
          <div className="analytics-card-loading">
            <div className="loading-spinner" />
            <span>Checking momentum...</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  const Icon = momentum.direction === 'up' ? TrendingUp : momentum.direction === 'down' ? TrendingDown : Minus;
  const iconColor = momentum.direction === 'up' ? 'var(--success)' : momentum.direction === 'down' ? 'var(--warning)' : 'var(--muted)';

  return (
    <Card className="analytics-card compliance-momentum-card">
      <CardHeader title="Compliance momentum" subtitle="Last 30 days" />
      <CardBody>
        <div className="compliance-momentum-content">
          <div className="compliance-momentum-indicator" style={{ color: iconColor }}>
            <Icon size={28} />
            {momentum.direction !== 'flat' && (
              <span className="compliance-momentum-delta">
                {momentum.delta > 0 ? '+' : ''}{momentum.delta}
              </span>
            )}
          </div>
          <p className="compliance-momentum-summary">{momentum.summary}</p>
        </div>
      </CardBody>
    </Card>
  );
}
